import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import straivLogo from '@/assets/straiv-logo-share.png';
import { ShareableWorkout } from '../ShareWorkoutCard';
import { RunningRouteVector, RouteCoordinate } from './RunningRouteVector';
import { useUnitFormatters } from '@/hooks/useUnits';
import { formatDistanceValue, distanceUnitShort, formatPaceDisplay, paceUnitLabel } from '@/lib/units';

interface ShareTemplateProps {
  workout: ShareableWorkout;
}

const ACCENT = '#0AFFC2';

const formatTimeShort = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  const s = Math.round(totalSeconds % 60);
  if (m > 0 && s > 0) return `${m}m ${s}s`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

const formatPaceExact = (paceSeconds: number) => {
  if (!paceSeconds || !isFinite(paceSeconds) || paceSeconds <= 0) return '--:--';
  const m = Math.floor(paceSeconds / 60);
  const s = Math.round(paceSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Compact Strava-style transparent overlay: Distance / Pace / Time + route + Straiv mark.
 * Designed to sit as a small editable block on a personal photo.
 */
export const ShareTemplateSmallTransparent = forwardRef<HTMLDivElement, ShareTemplateProps>(
  ({ workout }, ref) => {
    const { t } = useTranslation();
    const { units } = useUnitFormatters();
    const routeCoordinates: RouteCoordinate[] = (workout as any).route_coordinates || [];
    const durationSec =
      (workout as any).duration_seconds != null
        ? Number((workout as any).duration_seconds)
        : Math.round((workout.duration_minutes || 0) * 60);
    const paceSec =
      (workout as any).pace_seconds_per_km != null
        ? Number((workout as any).pace_seconds_per_km)
        : null;
    const distanceKm = workout.distance_km != null ? Number(workout.distance_km) : 0;
    const distDisplay = formatDistanceValue(distanceKm, units);
    const distUnit = distanceUnitShort(units);
    const paceUnit = paceUnitLabel(units);

    const labelStyle: React.CSSProperties = {
      fontSize: '36px',
      fontWeight: 500,
      letterSpacing: '0.02em',
      color: 'rgba(255,255,255,0.85)',
      marginBottom: '8px',
      lineHeight: 1,
    };
    const valueStyle: React.CSSProperties = {
      fontSize: '96px',
      fontWeight: 700,
      color: '#FFFFFF',
      lineHeight: 1.05,
      marginBottom: '40px',
    };

    return (
      <div
        ref={ref}
        className="relative overflow-hidden flex flex-col"
        style={{
          width: '1080px',
          height: '1980px',
          background: 'transparent',
          padding: '120px 96px',
          boxSizing: 'border-box',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <p style={labelStyle}>{t('components.shareTemplates.distance')}</p>
          <p style={valueStyle}>{distDisplay} {distUnit}</p>

          <p style={labelStyle}>{t('components.shareTemplates.pace')}</p>
          <p style={valueStyle}>
            {paceSec != null
              ? `${formatPaceDisplay(paceSec, units)} ${paceUnit}`
              : `${workout.average_pace || '--:--'} ${paceUnit}`}
          </p>

          <p style={labelStyle}>{t('components.shareTemplates.time')}</p>
          <p style={{ ...valueStyle, marginBottom: '56px' }}>
            {durationSec > 0 ? formatTimeShort(durationSec) : '--'}
          </p>

          {routeCoordinates.length >= 2 && (
            <div style={{ marginBottom: '64px', marginLeft: '-56px', alignSelf: 'flex-start' }}>
              <RunningRouteVector
                coordinates={routeCoordinates}
                width={720}
                height={480}
                strokeColor={ACCENT}
                strokeWidth={4}
                showStartEnd={false}
              />
            </div>
          )}

          <img
            src={straivLogo}
            alt="STRAIV"
            style={{ width: '380px', height: 'auto', alignSelf: 'flex-start', marginTop: '8px' }}
          />
        </div>
      </div>
    );
  }
);

ShareTemplateSmallTransparent.displayName = 'ShareTemplateSmallTransparent';
